import { Alert, Flex, Heading, InlineList, Text, View } from "@instructure/ui";
import c3 from "c3";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import SimpleTable from "../../../../../sections/Cards/SimpleTable/SimpleTable";
import { ExternalService } from "../../../../../services/ExternalService";
import ChartColors from "../../../../../utils/ChartColors";
import { sort } from "../../../../../utils/utils";
import {
  ERROR,
  useNotification,
} from "../../../../CustomHooks/NotificationProvider";
import RichTextViewer from "../../../../RichTextArea/RichTextViewer";
import { CANVAS, EXTERNAL } from "../../../Plan/utils";
import PresentationModeSelector from "./PresentationModeSelector";
import "./resultTable.css";
import CompletionStatusIndicator from "./StatusIndicators/CompletionStatusIndicator";
import ExternalStatusIndicator from "./StatusIndicators/ExternalStatusIndicator";
import { transformResultsToTableData } from "./utils/transformResultsToTableData";

const AssessmentChart = ({
  measure,
  filters,
  triggerMap,
  canPersist,
  approverEmail,
  isPreview,
  canvasTerms,
  insightsTerms,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [title, setTitle] = useState("");
  const [loadingChart, setLoadingChart] = useState(true);
  const [error, setError] = useState(false);
  const [noData, setNoData] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [submissionsGraded, setSubmissionsGraded] = useState(0);
  const [currentMode, setCurrentMode] = useState(
    measure.presentation_mode || "bar_chart"
  );
  const [termsCompleted, setTermsCompleted] = useState(0);

  async function handleModeChange(newMode) {
    const previousMode = currentMode;
    setCurrentMode(newMode);

    if (canPersist) {
      try {
        await ExternalService.patchAssessment(
          measure.id,
          { presentation_mode: newMode },
          approverEmail
        );
      } catch {
        setCurrentMode(previousMode);
        showNotification(t("results.persistModeError"), ERROR);
      }
    }
  }

  useEffect(() => {
    const getGraphData = async () => {
      try {
        setLoadingChart(true);
        if (measure.assignment_evidence.length > 0) {
          setTitle(
            filters.masteryResults === "true"
              ? t("assessment.masteryAvg")
              : t("assessment.gradeAvg")
          );
          let context = "";
          if (measure.type === CANVAS) {
            context = "canvas_course_embedded";
          } else if (measure.type === EXTERNAL) {
            context = "external_assessment";
          }
          const data = await ExternalService.getAssessmentResults(
            measure.assignment_evidence,
            filters.mode,
            filters.masteryResults,
            context
          );
          const results = data.results;
          if (context === "external_assessment") {
            setSubmissionsGraded(data.evidence_graded);
          } else if (context === "canvas_course_embedded") {
            const uniqueTerms = [];
            for (const result of results[0]) {
              let termName;
              if (filters.mode === "term") {
                termName = result.name.split(":")[0].trim();
              } else if (filters.mode === "course") {
                termName = result.name.split(":")[1].trim();
              }
              if (!uniqueTerms.includes(termName)) {
                uniqueTerms.push(termName);
              }
            }
            setTermsCompleted(uniqueTerms.length);
          }

          if (results[0].length === 0) {
            setNoData(true);
          } else {
            setNoData(false);
            results[0].forEach((term) => {
              if (context === "external_assessment") {
                // Show insights term in graph
                const canvasTerm = canvasTerms.find(
                  (t) => t.name === term["name"]
                );
                const insightsTerm = insightsTerms.find(
                  (t) => t.id === canvasTerm.insights_term_id
                );
                term["name"] = insightsTerm.name;
              }

              for (const key in term) {
                if (key !== "name" && term[key] !== null) {
                  term[key] = Number(term[key].toFixed(2));
                }
              }
            });
            results[1] = sort(results[1]);
            setGraphData(results);
          }
        } else {
          setNoData(true);
        }
      } catch {
        setError(true);
      } finally {
        setLoadingChart(false);
      }
    };
    getGraphData();
  }, [measure, triggerMap]);

  useEffect(() => {
    if (graphData && currentMode === "bar_chart") {
      c3.generate({
        bindto: document.getElementById("assessment-chart-" + measure.id),
        padding: { bottom: 25, left: 65 },
        color: { pattern: ChartColors },
        legend: { show: false },
        size: { height: 240 },
        data: {
          type: "bar",
          json: graphData[0],
          keys: { x: "name", value: graphData[1] },
        },
        zoom: { enabled: true },
        axis: {
          x: {
            type: "category", // this needed to load string x value
          },
          y: {
            label: {
              text:
                filters.masteryResults === "false"
                  ? t("dashboard.avgAssignmentGrade")
                  : t("dashboard.percentAchievedMastery"),
              position: "outer-middle",
            },
            min: 5,
            max: 100,
            tick: {
              values: [0, 20, 40, 60, 80, 100],
              format: function (d) {
                return d + "%";
              },
            },
          },
        },
        tooltip: {
          order: function (data1, data2) {
            return sort([data1, data2], (d) => d.name);
          },
        },
      });
    }
  }, [graphData, measure.id, currentMode]);

  const chartTitle = (measure) => {
    if (measure.type === EXTERNAL) {
      return t("assessment.externalResults");
    } else if (measure.type === CANVAS) {
      return t("results.targetedCanvasAssessment");
    }
  };

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item>
        <Heading level="h3">{chartTitle(measure)}</Heading>
      </Flex.Item>
      {!loadingChart && (
        <Flex.Item shouldGrow>
          {measure.type === EXTERNAL ? (
            <ExternalStatusIndicator
              submissionsGraded={submissionsGraded}
              purlId={measure.purl_id}
            />
          ) : (
            <CompletionStatusIndicator
              termsCompleted={termsCompleted}
              totalTermsPlanned={measure.terms.length}
            />
          )}
        </Flex.Item>
      )}
      {measure.description && (
        <Flex.Item size="100%" padding="0 0 0 large">
          <RichTextViewer
            value={measure.description}
            ariaLabel={t("assessment.assignmentDesc")}
          />
        </Flex.Item>
      )}
      {loadingChart ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Skeleton style={{ height: 300, borderRadius: 5 }} />
        </Flex.Item>
      ) : error ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="error">
            {t("notifications.general.fetchError", {
              obj: t("results.assessmentResults"),
            })}
          </Alert>
        </Flex.Item>
      ) : noData ? (
        <Flex.Item size="100%" padding="0 0 0 large">
          <Alert variant="info">{t("assessment.noResultsFound")}</Alert>
        </Flex.Item>
      ) : (
        <>
          {!isPreview && (
            <Flex.Item size="100%" padding="0 0 0 large">
              <View as="div" width="300px">
                <PresentationModeSelector
                  contextId={measure.id}
                  currentMode={currentMode}
                  onModeChange={handleModeChange}
                />
              </View>
            </Flex.Item>
          )}
          {currentMode === "bar_chart" && (
            <>
              <Flex.Item size="100%" padding="small 0 0 large">
                <View textAlign="center">
                  <Heading level="h4">{title}</Heading>
                  <div
                    id={"assessment-chart-" + measure.id}
                    style={{ margin: "10px 0 -20px 0", width: "100%" }}
                  />
                </View>
              </Flex.Item>
              <Flex.Item size="100%" padding="0 0 medium xx-large">
                <View as="div" textAlign="center">
                  <InlineList delimiter="pipe" isUnstyled>
                    {graphData[1].map((category, index) => (
                      <InlineList.Item key={index}>
                        <div
                          style={{
                            display: "inline-block",
                            height: 10,
                            width: 10,
                            marginRight: 8,
                            backgroundColor: ChartColors[index],
                          }}
                        />
                        <Text as="span" size="small">
                          {category}
                        </Text>
                      </InlineList.Item>
                    ))}
                  </InlineList>
                </View>
              </Flex.Item>
            </>
          )}
          {currentMode === "table" && (
            <Flex.Item size="100%" padding="0 0 medium large">
              <div
                aria-live="polite"
                aria-atomic="true"
                className="result-table-class"
              >
                {(() => {
                  const tableProps = transformResultsToTableData(
                    graphData,
                    title
                  );
                  return (
                    <SimpleTable
                      data={tableProps.data}
                      caption={<Text weight="bold">{tableProps.caption}</Text>}
                      rowTitle={tableProps.rowTitle}
                    />
                  );
                })()}
              </div>
            </Flex.Item>
          )}
        </>
      )}
    </Flex>
  );
};

export default AssessmentChart;
